<template>
  <div class="xcx-qrcode">
    <!-- 顶部说明：这个页面的定位与 scene 约束，避免同事误用 -->
    <el-alert
        class="tip"
        type="info"
        :closable="false"
        show-icon
        title="小程序码生成与管理"
    >
      <template #default>
        <div class="tip-body">
          填「用途文字」+ 选「目标页面路径」→ 一键生成小程序码。<br/>
          <span class="tip-warn">
            scene 仅支持：数字、大小写英文及 <code>!#$&amp;'()*+,/:;=?@-._~</code>，最长 32 字符（微信硬限制）；
            页面路径为小程序内路径，不能带前导 <code>/</code>、不能带 <code>?</code> 参数。
          </span>
        </div>
      </template>
    </el-alert>

    <!-- 生成区 -->
    <el-card class="gen-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span class="card-title">生成新小程序码</span>
          <el-button type="primary" :loading="generating" @click="handleGenerate">
            生成小程序码
          </el-button>
        </div>
      </template>

      <el-form :model="form" label-width="110px" class="gen-form">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="用途文字" required>
              <el-input
                  v-model="form.purpose"
                  maxlength="128"
                  show-word-limit
                  placeholder="如：兼职花名册录入（入职引导物料）"
                  clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="scene">
              <el-input
                  v-model="form.scene"
                  maxlength="32"
                  show-word-limit
                  placeholder="选填，如 part / full / cooperation"
                  clearable
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="从菜单选择">
              <el-select
                  v-model="form.menuId"
                  filterable
                  clearable
                  placeholder="选一个菜单，自动带出路径（选填）"
                  style="width: 100%"
                  @change="onMenuChange"
              >
                <el-option
                    v-for="m in menuOptions"
                    :key="m.id"
                    :label="m.label + ' [' + m.url + ']'"
                    :value="m.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="备注">
              <el-input v-model="form.remark" maxlength="255" placeholder="选填" clearable/>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="目标页面路径" required>
          <el-input
              v-model="form.pagePath"
              placeholder="如 subPackages/coach/pages/roster/identityVerify/identityVerify"
              clearable
          >
            <template #prepend>/</template>
          </el-input>
          <div class="form-hint">
            前导斜杠已由输入框前缀固定；提交时后端会再兜底剥掉，不必担心。
          </div>
        </el-form-item>
      </el-form>

      <div v-if="lastQr.qrImgUrl" class="last-qr">
        <div class="last-qr-title">刚刚生成：</div>
        <el-image
            :src="lastQr.qrImgUrl"
            :preview-src-list="[lastQr.qrImgUrl]"
            fit="contain"
            class="last-qr-img"
        />
        <div class="last-qr-meta">
          <div>页面：<code>{{ lastQr.page }}</code></div>
          <div>scene：<code>{{ lastQr.scene || '（空）' }}</code></div>
        </div>
        <el-button size="small" @click="copyText(lastQr.qrImgUrl)">复制图片地址</el-button>
      </div>
    </el-card>

    <!-- 列表区 -->
    <el-card class="list-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span class="card-title">已生成小程序码</span>
          <div class="list-filters">
            <el-input
                v-model="query.keyword"
                placeholder="用途 / 路径 / scene"
                style="width: 220px"
                clearable
                @clear="handleSearch"
                @keyup.enter="handleSearch"
            />
            <el-select v-model="query.status" placeholder="全部状态" clearable style="width: 130px" @change="handleSearch">
              <el-option label="启用" :value="1"/>
              <el-option label="停用" :value="0"/>
            </el-select>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
            <el-button @click="handleReset">重置</el-button>
          </div>
        </div>
      </template>

      <el-table
          v-loading="tableLoading"
          :data="tableData"
          border
          stripe
          height="520"
          header-cell-class-name="table-header"
      >
        <el-table-column label="小程序码" width="110" align="center">
          <template #default="scope">
            <el-image
                v-if="scope.row.qrImgUrl"
                :src="scope.row.qrImgUrl"
                :preview-src-list="[scope.row.qrImgUrl]"
                fit="contain"
                style="width: 64px; height: 64px"
            >
              <template #error>
                <div class="image-slot">加载失败</div>
              </template>
            </el-image>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column prop="purpose" label="用途" min-width="200" show-overflow-tooltip/>

        <el-table-column label="目标页面路径" min-width="300" show-overflow-tooltip>
          <template #default="scope">
            <code class="path-code">/{{ scope.row.pagePath }}</code>
          </template>
        </el-table-column>

        <el-table-column label="关联菜单" min-width="140" show-overflow-tooltip>
          <template #default="scope">
            <span v-if="scope.row.menuWords">{{ scope.row.menuWords }}</span>
            <span v-else-if="scope.row.menuMatchCount > 0" class="muted">
              未指定（库中匹配 {{ scope.row.menuMatchCount }} 条）
            </span>
            <span v-else class="muted">无（非菜单页）</span>
          </template>
        </el-table-column>

        <el-table-column label="scene" width="140" show-overflow-tooltip>
          <template #default="scope">
            <code v-if="scope.row.scene">{{ scope.row.scene }}</code>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>

        <el-table-column label="扫码次数" width="110" align="center">
          <template #default="scope">
            <el-tooltip
                v-if="!scope.row.scene"
                content="该码没有 scene，无法与扫码日志关联统计"
                placement="top"
            >
              <span class="muted">-</span>
            </el-tooltip>
            <el-tooltip
                v-else
                content="口径：扫码日志 qr_code_scan_log 中 source_str 等于本码 scene 的记录数。无页面维度，scene 重复时该值会偏大"
                placement="top"
            >
              <span :class="{ 'scan-zero': !scope.row.scanCount }">
                {{ scope.row.scanCount || 0 }}
              </span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="90" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.status === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="createUserName" label="创建人" width="110" show-overflow-tooltip/>

        <el-table-column prop="createTime" label="创建时间" width="170"/>

        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template #default="scope">
            <el-button
                v-if="scope.row.status === 1"
                type="text"
                size="small"
                style="color: #E6A23C"
                @click="handleStatus(scope.row, 0)"
            >停用</el-button>
            <el-button
                v-else
                type="text"
                size="small"
                style="color: #67C23A"
                @click="handleStatus(scope.row, 1)"
            >启用</el-button>

            <el-button type="text" size="small" @click="copyText(scope.row.qrImgUrl)">复制地址</el-button>

            <el-popconfirm
                title="确认删除这条生成记录？（图片文件仍在，记录不再展示）"
                confirm-button-text="删除"
                cancel-button-text="取消"
                @confirm="handleDelete(scope.row)"
            >
              <template #reference>
                <el-button type="text" size="small" style="color: #F56C6C">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
            background
            layout="total, prev, pager, next, sizes"
            :total="pageTotal"
            :current-page="query.pageIndex"
            :page-size="query.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import {menuPaths, generate, page, setStatus, delQrcode} from '@/api/xcxQrcode';
import {normalizeQrImgUrl} from '@/utils/qrImgUrl';

export default {
  name: 'XcxQrcode',
  data() {
    return {
      form: {
        purpose: '',
        pagePath: '',
        menuId: null,
        scene: '',
        remark: ''
      },
      lastQr: {},
      generating: false,
      menuOptions: [],
      query: {
        pageIndex: 1,
        pageSize: 20,
        keyword: '',
        status: null
      },
      tableData: [],
      pageTotal: 0,
      tableLoading: false
    };
  },
  created() {
    this.loadMenuOptions();
    this.getData();
  },
  methods: {
    /** 加载可选菜单路径 */
    loadMenuOptions() {
      menuPaths()
          .then(res => {
            if (res && res.code === 200) {
              this.menuOptions = res.data || [];
            }
          })
          .catch(() => {
          });
    },

    /** 选了菜单 → 自动带出路径（保留菜单 id 供列表显示菜单名） */
    onMenuChange(id) {
      if (id == null) return;
      const hit = this.menuOptions.find(m => m.id === id);
      if (hit && hit.url) {
        // 后端 page_path 约定不带前导斜杠
        this.form.pagePath = String(hit.url).replace(/^\/+/, '');
      }
    },

    /** 生成 */
    handleGenerate() {
      if (!this.form.purpose || !this.form.purpose.trim()) {
        this.$message.warning('请填写用途文字');
        return;
      }
      if (!this.form.pagePath || !this.form.pagePath.trim()) {
        this.$message.warning('请填写或选择目标页面路径');
        return;
      }
      const scene = (this.form.scene || '').trim();
      if (scene.length > 32) {
        this.$message.warning('scene 最长 32 个字符');
        return;
      }
      this.generating = true;
      generate({
        purpose: this.form.purpose.trim(),
        pagePath: this.form.pagePath.trim(),
        menuId: this.form.menuId,
        scene: scene,
        remark: this.form.remark
      })
          .then(res => {
            this.generating = false;
            if (res && res.code === 200) {
              this.$message.success('生成成功');
              // 后端已做域名归一，这里再兜一层，保证后端未部署时也能正常预览
              this.lastQr = res.data || {};
              if (this.lastQr.qrImgUrl) {
                this.lastQr.qrImgUrl = normalizeQrImgUrl(this.lastQr.qrImgUrl);
              }
              this.getData();
            } else {
              this.$message.error((res && res.message) || '生成失败');
            }
          })
          .catch(() => {
            this.generating = false;
            this.$message.error('生成失败，请稍后重试');
          });
    },

    /** 列表 */
    getData() {
      this.tableLoading = true;
      page({
        pageIndex: this.query.pageIndex,
        pageSize: this.query.pageSize,
        keyword: this.query.keyword,
        status: this.query.status
      })
          .then(res => {
            this.tableLoading = false;
            if (res && res.code === 200) {
              this.tableData = (res.data && res.data.records) || [];
              // 兜底归一域名：后端未部署 / 历史数据为 IP:端口 时，图片仍可正常显示
              this.tableData = this.tableData.map(row => ({
                ...row,
                qrImgUrl: normalizeQrImgUrl(row.qrImgUrl)
              }));
              this.pageTotal = (res.data && res.data.dataCount) || 0;
            } else {
              this.$message.error((res && res.message) || '获取列表失败');
            }
          })
          .catch(() => {
            this.tableLoading = false;
          });
    },

    handleSearch() {
      this.query.pageIndex = 1;
      this.getData();
    },

    handleReset() {
      this.query.keyword = '';
      this.query.status = null;
      this.query.pageIndex = 1;
      this.getData();
    },

    handlePageChange(val) {
      this.query.pageIndex = val;
      this.getData();
    },

    handleSizeChange(val) {
      this.query.pageSize = val;
      this.query.pageIndex = 1;
      this.getData();
    },

    handleStatus(row, status) {
      setStatus(row.id, status)
          .then(res => {
            if (res && res.code === 200) {
              this.$message.success(status === 1 ? '已启用' : '已停用');
              this.getData();
            } else {
              this.$message.error((res && res.message) || '操作失败');
            }
          })
          .catch(() => this.$message.error('操作失败'));
    },

    handleDelete(row) {
      delQrcode(row.id)
          .then(res => {
            if (res && res.code === 200) {
              this.$message.success('已删除');
              this.getData();
            } else {
              this.$message.error((res && res.message) || '删除失败');
            }
          })
          .catch(() => this.$message.error('删除失败'));
    },

    /** 复制文本（http 环境下 navigator.clipboard 不可用，走 textarea 兜底） */
    copyText(text) {
      if (!text) {
        this.$message.warning('没有可复制的地址');
        return;
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
            .then(() => this.$message.success('已复制'))
            .catch(() => this.fallbackCopy(text));
      } else {
        this.fallbackCopy(text);
      }
    },

    fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        this.$message.success('已复制');
      } catch (e) {
        this.$message.warning('复制失败，请手动选择复制');
      }
      document.body.removeChild(ta);
    }
  }
};
</script>

<style scoped>
.xcx-qrcode {
  padding: 12px 16px;
}

.tip {
  margin-bottom: 12px;
}

.tip-body {
  line-height: 1.7;
}

.tip-warn {
  color: #E6A23C;
}

.gen-card,
.list-card {
  margin-bottom: 12px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  font-weight: 600;
  font-size: 15px;
}

.gen-form {
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin-top: 2px;
}

.last-qr {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  background: #fafafa;
}

.last-qr-title {
  font-weight: 600;
  color: #606266;
}

.last-qr-img {
  width: 120px;
  height: 120px;
}

.last-qr-meta {
  font-size: 13px;
  color: #606266;
  line-height: 1.9;
  word-break: break-all;
}

.list-filters {
  display: flex;
  align-items: center;
  gap: 8px;
}

.path-code {
  font-size: 12px;
  color: #409EFF;
  word-break: break-all;
}

.muted {
  color: #C0C4CC;
}

.scan-zero {
  color: #C0C4CC;
}

.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
